/**
 * markdown.js — Akili Brain: Markdown → HTML renderer
 *
 * Lightweight, zero-dependency converter. Supports:
 *   **bold**, *italic*, `inline code`
 *   # / ## / ### headings
 *   - / * unordered lists  •  1. ordered lists
 *   Paragraphs & single-line <br> breaks
 *
 * Security: all raw text is HTML-escaped FIRST; only our own tags are
 * injected afterward, so innerHTML usage is safe.
 */

// ── Internal escape ───────────────────────────────────────────────────────────

function _esc(str) {
  return String(str)
    .replace(/&/g,  '&amp;')
    .replace(/</g,  '&lt;')
    .replace(/>/g,  '&gt;')
    .replace(/"/g,  '&quot;');
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Convert a Markdown string to safe HTML.
 * @param {string} text  Raw text (possibly containing Markdown syntax)
 * @returns {string}     HTML string ready for innerHTML
 */
export function renderMarkdown(text) {
  if (!text) return '';

  // ── Step 1: Escape all existing HTML ─────────────────────────────────────
  let html = _esc(text);

  // ── Step 2: Headings  (### / ## / #) ─────────────────────────────────────
  html = html.replace(/^### (.+)$/gm, '<h4>$1</h4>');
  html = html.replace(/^## (.+)$/gm,  '<h3>$1</h3>');
  html = html.replace(/^# (.+)$/gm,   '<h2>$1</h2>');

  // ── Step 3: Bold  (**text**) ──────────────────────────────────────────────
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

  // ── Step 4: Italic  (*text*) — must not match list markers ───────────────
  // Negative look-behind/ahead keeps "* item" safe.
  html = html.replace(/(?<![*\n])\*([^*\n]+)\*(?!\*)/g, '<em>$1</em>');

  // ── Step 5: Inline code  (`code`) ────────────────────────────────────────
  html = html.replace(/`([^`]+)`/g, '<code class="inline-code">$1</code>');

  // ── Step 6: Unordered lists  (lines starting with "- " or "* ") ──────────
  html = html.replace(
    /((?:^|\n)[-*] .+?)(?=\n\n|\n[^-*\s]|$)/gs,
    (match) => {
      const items = match
        .trim()
        .split('\n')
        .filter(Boolean)
        .map(line => '<li>' + line.replace(/^[-*] /, '') + '</li>')
        .join('');
      return '<ul>' + items + '</ul>';
    },
  );

  // ── Step 7: Ordered lists  (lines starting with "N. ") ───────────────────
  html = html.replace(
    /((?:^|\n)\d+\. .+?)(?=\n\n|\n[^\d\s]|$)/gs,
    (match) => {
      const items = match
        .trim()
        .split('\n')
        .filter(Boolean)
        .map(line => '<li>' + line.replace(/^\d+\. /, '') + '</li>')
        .join('');
      return '<ol>' + items + '</ol>';
    },
  );

  // ── Step 8: Paragraphs & line breaks ─────────────────────────────────────
  // Split on blank lines; wrap prose blocks in <p>; keep headings/lists bare.
  html = html
    .split(/\n{2,}/)
    .map(block => block.trim())
    .filter(Boolean)
    .map(block => {
      if (/^<(h[2-4]|ul|ol)/.test(block)) return block;
      // Single newlines inside a prose block → <br>
      return '<p>' + block.replace(/\n/g, '<br>') + '</p>';
    })
    .join('\n');

  return html;
}
